// Localização: frontend/src/components/Escalas/EstatisticasDetalhadas.jsx
// Componente para exibir estatísticas detalhadas da escala de plantão

import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import {
    TrendingUp,
    Clock,
    Users,
    Calendar,
    Activity,
    BarChart3,
    PieChart as PieChartIcon
} from 'lucide-react';

const COLORS = {
    diurno: '#3b82f6',      // blue-500
    noturno: '#8b5cf6',     // purple-500
    integral: '#10b981',    // emerald-500
    madrugada: '#6366f1',   // indigo-500
    default: '#6b7280'      // gray-500
};

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/**
 * Componente de Estatísticas Detalhadas da Escala
 * @param {Object} props
 * @param {Array} props.itens - Itens da escala
 * @param {Object} props.estatisticas - Estatísticas gerais
 * @param {Object} props.periodo - Período {inicio, fim}
 */
const EstatisticasDetalhadas = ({ itens = [], estatisticas = {}, periodo = {} }) => {
    const [activeTab, setActiveTab] = useState('geral');

    // Calcular horas por profissional
    const horasPorProfissional = useMemo(() => {
        const horas = {};
        itens.forEach(item => {
            const inicio = moment(`${item.data_plantao} ${item.hora_inicio}`, 'YYYY-MM-DD HH:mm:ss');
            let fim = moment(`${item.data_plantao} ${item.hora_fim}`, 'YYYY-MM-DD HH:mm:ss');
            
            // Se for plantão noturno/integral, ajustar para o próximo dia
            if (item.tipo_plantao === 'noturno' || item.tipo_plantao === 'integral') {
                if (fim.isBefore(inicio)) {
                    fim.add(1, 'day');
                }
            }
            
            const horasItem = fim.diff(inicio, 'hours', true);
            const nome = item.usuario_nome || 'N/A';
            
            if (!horas[nome]) {
                horas[nome] = 0;
            }
            horas[nome] += horasItem;
        });

        return Object.entries(horas)
            .map(([nome, total]) => ({ nome, horas: Math.round(total * 10) / 10 }))
            .sort((a, b) => b.horas - a.horas);
    }, [itens]);

    // Distribuição por tipo de plantão
    const distribuicaoPorTipo = useMemo(() => {
        const tipos = {};
        itens.forEach(item => {
            const tipo = item.tipo_plantao || 'outro';
            tipos[tipo] = (tipos[tipo] || 0) + 1;
        });

        return Object.entries(tipos).map(([tipo, quantidade]) => ({
            nome: tipo.charAt(0).toUpperCase() + tipo.slice(1),
            quantidade,
            porcentagem: ((quantidade / itens.length) * 100).toFixed(1)
        }));
    }, [itens]);

    // Distribuição por dia da semana
    const distribuicaoPorDiaSemana = useMemo(() => {
        const dias = {};
        itens.forEach(item => {
            const diaSemana = moment(item.data_plantao).day();
            const nomeDia = DIAS_SEMANA[diaSemana];
            
            if (!dias[nomeDia]) {
                dias[nomeDia] = 0;
            }
            dias[nomeDia] += 1;
        });

        // Garantir ordem dos dias da semana
        return DIAS_SEMANA.map(dia => ({
            dia,
            quantidade: dias[dia] || 0
        }));
    }, [itens]);

    // Distribuição por paciente
    const distribuicaoPorPaciente = useMemo(() => {
        const pacientes = {};
        itens.forEach(item => {
            const nome = item.paciente_nome || 'Não especificado';
            pacientes[nome] = (pacientes[nome] || 0) + 1;
        });

        return Object.entries(pacientes)
            .map(([nome, quantidade]) => ({ nome, quantidade }))
            .sort((a, b) => b.quantidade - a.quantidade)
            .slice(0, 10); // Top 10
    }, [itens]);

    // Carga horária semanal por profissional
    const cargaHorariaSemanal = useMemo(() => {
        const carga = {};
        itens.forEach(item => {
            const inicio = moment(`${item.data_plantao} ${item.hora_inicio}`, 'YYYY-MM-DD HH:mm:ss');
            let fim = moment(`${item.data_plantao} ${item.hora_fim}`, 'YYYY-MM-DD HH:mm:ss');
            
            if (item.tipo_plantao === 'noturno' || item.tipo_plantao === 'integral') {
                if (fim.isBefore(inicio)) {
                    fim.add(1, 'day');
                }
            }
            
            const horasItem = fim.diff(inicio, 'hours', true);
            const nome = item.usuario_nome || 'N/A';
            const semana = moment(item.data_plantao).week();
            const ano = moment(item.data_plantao).year();
            const chave = `${nome}_${ano}_${semana}`;
            
            if (!carga[chave]) {
                carga[chave] = { nome, semana, horas: 0 };
            }
            carga[chave].horas += horasItem;
        });

        return Object.values(carga)
            .map(item => ({ ...item, horas: Math.round(item.horas * 10) / 10 }))
            .sort((a, b) => {
                if (a.nome !== b.nome) return a.nome.localeCompare(b.nome);
                return a.semana - b.semana;
            });
    }, [itens]);

    // Estatísticas gerais calculadas
    const statsGerais = useMemo(() => {
        const totalHoras = horasPorProfissional.reduce((sum, p) => sum + p.horas, 0);
        const mediaHoras = horasPorProfissional.length > 0 
            ? totalHoras / horasPorProfissional.length 
            : 0;
        const maxHoras = horasPorProfissional.length > 0 
            ? Math.max(...horasPorProfissional.map(p => p.horas)) 
            : 0;
        const minHoras = horasPorProfissional.length > 0 
            ? Math.min(...horasPorProfissional.map(p => p.horas)) 
            : 0;

        return {
            totalPlantoes: itens.length,
            totalProfissionais: horasPorProfissional.length,
            totalHoras: Math.round(totalHoras * 10) / 10,
            mediaHoras: Math.round(mediaHoras * 10) / 10,
            maxHoras: Math.round(maxHoras * 10) / 10,
            minHoras: Math.round(minHoras * 10) / 10
        };
    }, [itens, horasPorProfissional]);

    const tabs = [
        { id: 'geral', label: 'Geral', icon: Activity },
        { id: 'profissionais', label: 'Profissionais', icon: Users },
        { id: 'distribuicao', label: 'Distribuição', icon: BarChart3 },
        { id: 'temporal', label: 'Temporal', icon: Calendar }
    ];

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            {/* Tabs */}
            <div className="border-b border-gray-200">
                <div className="flex overflow-x-auto">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'text-primary-600 border-b-2 border-primary-600'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Conteúdo das Tabs */}
            <div className="p-6">
                {/* Tab: Geral */}
                {activeTab === 'geral' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <div className="text-sm text-blue-600 mb-1">Total de Plantões</div>
                                <div className="text-2xl font-bold text-blue-900">{statsGerais.totalPlantoes}</div>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                <div className="text-sm text-purple-600 mb-1">Profissionais</div>
                                <div className="text-2xl font-bold text-purple-900">{statsGerais.totalProfissionais}</div>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                <div className="text-sm text-green-600 mb-1">Total de Horas</div>
                                <div className="text-2xl font-bold text-green-900">{statsGerais.totalHoras}h</div>
                            </div>
                            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                                <div className="text-sm text-indigo-600 mb-1">Média por Profissional</div>
                                <div className="text-2xl font-bold text-indigo-900">{statsGerais.mediaHoras}h</div>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                <div className="text-sm text-yellow-600 mb-1">Máximo</div>
                                <div className="text-2xl font-bold text-yellow-900">{statsGerais.maxHoras}h</div>
                            </div>
                            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                <div className="text-sm text-red-600 mb-1">Mínimo</div>
                                <div className="text-2xl font-bold text-red-900">{statsGerais.minHoras}h</div>
                            </div>
                        </div>

                        {/* Score de Qualidade */}
                        {estatisticas && (
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                    Scores de Qualidade
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-white rounded-lg p-3">
                                        <div className="text-sm text-gray-600 mb-1">Distribuição Equitativa</div>
                                        <div className="text-2xl font-bold text-blue-600">
                                            {estatisticas.distribuicao_equitativa_score 
                                                ? (estatisticas.distribuicao_equitativa_score * 100).toFixed(0) + '%'
                                                : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3">
                                        <div className="text-sm text-gray-600 mb-1">Preferências Atendidas</div>
                                        <div className="text-2xl font-bold text-purple-600">
                                            {estatisticas.preferencias_atendidas_score 
                                                ? (estatisticas.preferencias_atendidas_score * 100).toFixed(0) + '%'
                                                : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3">
                                        <div className="text-sm text-gray-600 mb-1">Conflitos</div>
                                        <div className={`text-2xl font-bold ${
                                            (estatisticas.conflitos_detectados || 0) > 0 ? 'text-red-600' : 'text-green-600'
                                        }`}>
                                            {estatisticas.conflitos_detectados || 0}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Profissionais */}
                {activeTab === 'profissionais' && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5 text-blue-600" />
                                Distribuição de Horas por Profissional
                            </h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={horasPorProfissional}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="nome" 
                                        angle={-45}
                                        textAnchor="end"
                                        height={100}
                                        interval={0}
                                    />
                                    <YAxis />
                                    <Tooltip 
                                        formatter={(value) => [`${value}h`, 'Horas']}
                                    />
                                    <Legend />
                                    <Bar dataKey="horas" fill="#3b82f6" name="Horas" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4">Detalhamento por Profissional</h4>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Profissional
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total de Horas
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Plantões
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Média por Plantão
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {horasPorProfissional.map((prof, index) => {
                                            const plantoesProf = itens.filter(i => i.usuario_nome === prof.nome).length;
                                            const media = plantoesProf > 0 ? (prof.horas / plantoesProf).toFixed(1) : 0;
                                            return (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {prof.nome}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                        {prof.horas}h
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                        {plantoesProf}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                        {media}h
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Distribuição */}
                {activeTab === 'distribuicao' && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <PieChartIcon className="w-5 h-5 text-purple-600" />
                                Distribuição por Tipo de Plantão
                            </h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={distribuicaoPorTipo}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ nome, porcentagem }) => `${nome}: ${porcentagem}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="quantidade"
                                    >
                                        {distribuicaoPorTipo.map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={COLORS[entry.nome.toLowerCase()] || COLORS.default} 
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {distribuicaoPorPaciente.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-4">Top 10 Pacientes</h4>
                                <div className="space-y-2">
                                    {distribuicaoPorPaciente.map((paciente, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                            <span className="text-sm font-medium text-gray-900">{paciente.nome}</span>
                                            <span className="text-sm text-gray-600">{paciente.quantidade} plantões</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Temporal */}
                {activeTab === 'temporal' && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-green-600" />
                                Distribuição por Dia da Semana
                            </h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={distribuicaoPorDiaSemana}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="dia" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="quantidade" fill="#10b981" name="Plantões" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EstatisticasDetalhadas;

